import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

interface Citation {
  source: string;
  source_type: string;
  detail: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  context_summary?: string;
  equipment_tag?: string | null;
  timestamp: Date;
  loading?: boolean;
}

interface EquipmentOption {
  id: string;
  equipment_tag: string;
  description: string | null;
}

const CITATION_TYPE_ICONS: Record<string, string> = {
  document: '📄',
  equipment: '⚙️',
  component: '🔧',
  work_order: '🔨',
  c1_inspection: '🔍',
};

const EXAMPLE_QUESTIONS = [
  'What are the overhaul dates for all Ex components?',
  'List all MDA items and their removal deadlines',
  'Summarise the risk assessment findings',
  'What maintenance work has been done in the last 6 months?',
  'Are there any C1 inspections with conditional passes?',
  'What is the compliance status of this equipment?',
  'Go through the documents and list any action items',
];

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const equipmentId = searchParams.get('equipment_id');
  const orgId = searchParams.get('org_id');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [equipment, setEquipment] = useState<EquipmentOption[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState(equipmentId || '');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Load equipment list for selector
    const loadEquipment = async () => {
      try {
        const { data } = await api.get('/api/equipment/', { params: { page_size: 100 } });
        setEquipment(data.items.map((e: any) => ({
          id: e.id, equipment_tag: e.equipment_tag, description: e.description,
        })));
      } catch (err) { console.error(err); }
    };
    loadEquipment();
  }, []);

  useEffect(() => {
    if (equipmentId) setSelectedEquipment(equipmentId);
  }, [equipmentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (question?: string) => {
    const q = question || input.trim();
    if (!q || loading) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: q,
      timestamp: new Date(),
    };

    const loadingMsg: ChatMessage = {
      id: `a-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      loading: true,
    };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data } = await api.post('/api/chat/ask', {
        question: q,
        equipment_id: selectedEquipment || null,
        org_id: orgId || null,
        include_documents: true,
      });

      setMessages(prev =>
        prev.map(m =>
          m.id === loadingMsg.id
            ? {
                ...m,
                content: data.answer,
                citations: data.citations,
                context_summary: data.context_summary,
                equipment_tag: data.equipment_tag,
                loading: false,
              }
            : m
        )
      );
    } catch (err: any) {
      setMessages(prev =>
        prev.map(m =>
          m.id === loadingMsg.id
            ? {
                ...m,
                content: `Error: ${err.response?.data?.detail || 'Failed to get response. Please try again.'}`,
                loading: false,
              }
            : m
        )
      );
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectedEq = equipment.find(e => e.id === selectedEquipment);

  return (
    <div style={s.container}>
      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <button onClick={() => navigate('/')} style={s.backBtn}>← Dashboard</button>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '36px' }} />
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={s.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Scope:</span>
          <select
            value={selectedEquipment}
            onChange={e => setSelectedEquipment(e.target.value)}
            style={s.eqSelect}
          >
            <option value="">All Equipment</option>
            {equipment.map(eq => (
              <option key={eq.id} value={eq.id}>
                {eq.equipment_tag} — {eq.description || 'No description'}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div style={s.chatContainer}>
        {/* Sidebar */}
        <div style={s.sidebar}>
          <div style={s.sidebarSection}>
            <div style={s.sidebarTitle}>🤖 AI Assistant</div>
            <p style={s.sidebarText}>
              Ask questions about your equipment, PSF documents, maintenance history, compliance status, and more.
              The AI reads your uploaded documents and cross-references all available data.
            </p>
          </div>

          {selectedEq && (
            <div style={s.sidebarSection}>
              <div style={s.sidebarTitle}>📌 Current Scope</div>
              <div style={s.scopeCard}>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#1a1a2e' }}>{selectedEq.equipment_tag}</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{selectedEq.description}</div>
              </div>
            </div>
          )}

          <div style={s.sidebarSection}>
            <div style={s.sidebarTitle}>💡 Example Questions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {EXAMPLE_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  disabled={loading}
                  style={s.exampleBtn}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div style={s.chatArea}>
          <div style={s.messageList}>
            {messages.length === 0 && (
              <div style={s.emptyChat}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
                <h2 style={{ margin: '0 0 8px', color: '#1a1a2e' }}>PSF AI Assistant</h2>
                <p style={{ color: '#888', fontSize: '14px', maxWidth: '400px', lineHeight: '1.5' }}>
                  Ask me anything about your equipment, compliance documents, maintenance history, or overhaul schedules.
                  {selectedEq && <><br/><br/>Currently scoped to <strong>{selectedEq.equipment_tag}</strong>.</>}
                </p>
              </div>
            )}

            {messages.map(msg => (
              <div key={msg.id} style={msg.role === 'user' ? s.userMsgRow : s.assistantMsgRow}>
                <div style={msg.role === 'user' ? s.userMsg : s.assistantMsg}>
                  {msg.loading ? (
                    <div style={s.loadingDots}>
                      <span style={s.dot}>●</span>
                      <span style={{ ...s.dot, animationDelay: '0.2s' }}>●</span>
                      <span style={{ ...s.dot, animationDelay: '0.4s' }}>●</span>
                      <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>Analysing PSF data...</span>
                    </div>
                  ) : (
                    <>
                      <div style={s.msgContent}>
                        {msg.content.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line.startsWith('- ') ? (
                              <div style={{ paddingLeft: '16px', position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '4px' }}>•</span>
                                {formatLine(line.slice(2))}
                              </div>
                            ) : line.startsWith('**') && line.endsWith('**') ? (
                              <div style={{ fontWeight: 700, marginTop: '8px' }}>{line.replace(/\*\*/g, '')}</div>
                            ) : line === '' ? (
                              <br />
                            ) : (
                              <div>{formatLine(line)}</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {msg.citations && msg.citations.length > 0 && (
                        <div style={s.citationsBox}>
                          <div style={s.citationsTitle}>📎 Sources ({msg.citations.length})</div>
                          {msg.citations.map((c, i) => (
                            <div key={i} style={s.citationItem}>
                              <span style={{ marginRight: '6px' }}>{CITATION_TYPE_ICONS[c.source_type] || '📄'}</span>
                              <span style={{ fontWeight: 500 }}>{c.source}</span>
                              {c.detail && <span style={{ color: '#888', marginLeft: '8px', fontSize: '11px' }}>— {c.detail}</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      {msg.context_summary && msg.role === 'assistant' && (
                        <div style={s.contextNote}>{msg.context_summary}</div>
                      )}
                    </>
                  )}
                </div>
                <div style={s.msgTime}>{msg.timestamp.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={s.inputArea}>
            <div style={s.inputRow}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={selectedEq ? `Ask about ${selectedEq.equipment_tag}...` : 'Ask about your PSF data...'}
                style={s.textInput}
                rows={1}
                disabled={loading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                style={{ ...s.sendBtn, opacity: (!input.trim() || loading) ? 0.5 : 1 }}
              >
                {loading ? '⏳' : '➤'}
              </button>
            </div>
            <div style={s.inputHint}>
              Press Enter to send • Shift+Enter for new line
              {selectedEq && <> • Scoped to <strong>{selectedEq.equipment_tag}</strong></>}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={s.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};

// Format inline markdown (bold)
const formatLine = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { padding: '12px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  backBtn: { padding: '6px 12px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  eqSelect: { padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '13px', minWidth: '250px' },

  chatContainer: { display: 'flex', flex: 1, overflow: 'hidden' },

  // Sidebar
  sidebar: { width: '280px', background: '#fff', borderRight: '1px solid #e0e0e0', padding: '20px', overflowY: 'auto' as const, flexShrink: 0 },
  sidebarSection: { marginBottom: '24px' },
  sidebarTitle: { fontSize: '13px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  sidebarText: { fontSize: '12px', color: '#666', lineHeight: '1.5', margin: 0 },
  scopeCard: { padding: '12px', background: '#f0f4ff', borderRadius: '8px', border: '1px solid #d0d8f0' },
  exampleBtn: { padding: '8px 12px', background: '#f8f9ff', border: '1px solid #e0e4f0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: '#333', textAlign: 'left' as const, lineHeight: '1.4', transition: 'all 0.15s' },

  // Chat area
  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  messageList: { flex: 1, overflowY: 'auto' as const, padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },

  emptyChat: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center' as const },

  userMsgRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  assistantMsgRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  userMsg: { maxWidth: '70%', padding: '12px 16px', background: '#0f3460', color: '#fff', borderRadius: '16px 16px 4px 16px', fontSize: '14px', lineHeight: '1.5' },
  assistantMsg: { maxWidth: '80%', padding: '16px 20px', background: '#fff', color: '#333', borderRadius: '16px 16px 16px 4px', fontSize: '14px', lineHeight: '1.6', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  msgContent: { whiteSpace: 'pre-wrap' as const },
  msgTime: { fontSize: '10px', color: '#bbb', marginTop: '4px', paddingLeft: '4px', paddingRight: '4px' },

  // Citations
  citationsBox: { marginTop: '12px', padding: '10px 14px', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e0e4f0' },
  citationsTitle: { fontSize: '11px', fontWeight: 700, color: '#666', marginBottom: '6px', textTransform: 'uppercase' as const },
  citationItem: { fontSize: '12px', color: '#444', padding: '3px 0', display: 'flex', alignItems: 'center' },
  contextNote: { marginTop: '8px', fontSize: '11px', color: '#aaa', fontStyle: 'italic' as const },

  // Loading
  loadingDots: { display: 'flex', alignItems: 'center' },
  dot: { color: '#0f3460', fontSize: '18px', animation: 'pulse 1.2s infinite', marginRight: '2px' },

  // Input
  inputArea: { padding: '16px 24px', background: '#fff', borderTop: '1px solid #e0e0e0' },
  inputRow: { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  textInput: { flex: 1, padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '14px', resize: 'none' as any, fontFamily: 'inherit', outline: 'none', minHeight: '24px', maxHeight: '120px' },
  sendBtn: { padding: '12px 20px', background: '#0f3460', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '18px', fontWeight: 700, flexShrink: 0, minWidth: '52px', height: '48px' },
  inputHint: { fontSize: '11px', color: '#bbb', marginTop: '6px', paddingLeft: '4px' },
};

// Add CSS animation for loading dots
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0%, 80%, 100% { opacity: 0.3; }
    40% { opacity: 1; }
  }
`;
document.head.appendChild(styleSheet);

export default Chat;
